import type { HeroStats, RawHeroStats, StatsData, GameMode, MapInfo, HeroRole } from "$lib/types";
import type { MapGameMode } from "$lib/types";
import { MAP_GAME_MODES, MAP_GAME_MODE_LABELS, MAP_GAME_MODE_ORDER } from "$lib/data/map-types";
import { assignTiers } from "$lib/utils/stats";
import { getMapImage } from "$lib/utils/images";
import { isPlusTier, computeWeightedStats } from "$lib/utils/ranks";
import { isInsufficientData } from "$lib/utils/formatting";

export { MAP_GAME_MODES, MAP_GAME_MODE_LABELS, MAP_GAME_MODE_ORDER };

/** Get the game mode type for a given map key. */
export function getMapType(mapKey: string): MapGameMode | undefined {
    return MAP_GAME_MODES[mapKey];
}

/** Get the display label for a map game mode type. */
export function getMapTypeLabel(type: MapGameMode): string {
    return MAP_GAME_MODE_LABELS[type];
}

/** CSS custom-property references for each map game mode, defined in app.css @theme. */
const MAP_TYPE_CSS_VAR: Record<MapGameMode, string> = {
    control: "var(--color-mode-control)",
    escort: "var(--color-mode-escort)",
    hybrid: "var(--color-mode-hybrid)",
    push: "var(--color-mode-push)",
    flashpoint: "var(--color-mode-flashpoint)",
    clash: "var(--color-mode-clash)",
};

/** Returns a CSS color value for the given map game mode. */
export function getMapTypeColor(type: MapGameMode): string {
    return MAP_TYPE_CSS_VAR[type] ?? "#888888";
}

/**
 * Tailwind border-color classes for each map game mode.
 * Uses the --color-mode-* tokens registered in @theme.
 */
const MAP_TYPE_ACCENT: Record<MapGameMode, string> = {
    control: "border-mode-control/40",
    escort: "border-mode-escort/40",
    hybrid: "border-mode-hybrid/40",
    push: "border-mode-push/40",
    flashpoint: "border-mode-flashpoint/40",
    clash: "border-mode-clash/40",
};

/** Get the Tailwind border-color accent class for a map game mode. */
export function getMapTypeAccent(type: MapGameMode): string {
    return MAP_TYPE_ACCENT[type] ?? "border-base-content/20";
}

/**
 * Get the list of maps that have data for the current game-mode and tier,
 * sorted alphabetically by display name.
 */
export function getAvailableMaps(statsData: StatsData, gameMode: GameMode, tier: string): MapInfo[] {
    const maps = statsData.maps;
    let filtered: MapInfo[];
    if (gameMode === "competitive") {
        // "+" tiers use the "All" tier's map set (same maps, just weighted differently)
        const lookupTier = isPlusTier(tier) ? "All" : tier;
        const tierData = statsData.data.competitive[lookupTier];
        if (!tierData) return [];
        filtered = maps.filter((m) => m.key in tierData);
    } else {
        filtered = maps.filter((m) => m.key in statsData.data.quickplay);
    }
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get the top heroes by win rate for a specific map, optionally filtered by role.
 * Returns an array of tiered hero stats sorted by win rate descending.
 */
export function getTopHeroesForMap(
    statsData: StatsData,
    gameMode: GameMode,
    tier: string,
    mapKey: string,
    role?: HeroRole,
    count = 3,
): HeroStats[] {
    const raw = getHeroData(statsData, gameMode, tier, mapKey);
    const filtered = role ? raw.filter((h) => h.role === role) : raw;
    const tiered = assignTiers(filtered);
    return tiered.sort((a, b) => b.winRate - a.winRate).slice(0, count);
}

/**
 * Get the raw hero stats for a given game-mode, tier, and map.
 * Falls back to the "all" map if the requested map has no data.
 * Supports "+" tier keys (e.g. "Gold+") via weighted computation.
 */
export function getHeroData(statsData: StatsData, gameMode: GameMode, tier: string, map = "all"): RawHeroStats[] {
    if (gameMode === "competitive") {
        if (isPlusTier(tier)) {
            if (tier === "Bronze+") {
                const allData = statsData.data.competitive["All"];
                if (!allData) return [];
                return allData[map] ?? allData["all"] ?? [];
            }
            return computeWeightedStats(statsData.data.competitive, tier, map);
        }
        const tierData = statsData.data.competitive[tier];
        if (!tierData) return [];
        return tierData[map] ?? tierData["all"] ?? [];
    }
    const qpData = statsData.data.quickplay;
    return qpData[map] ?? qpData["all"] ?? [];
}

export interface MapTypeStats {
    type: MapGameMode;
    label: string;
    winRate: number;
    pickRate: number;
    mapCount: number;
}

/**
 * Aggregate a hero's per-map stats by map game-mode type (Control, Escort, …).
 * Returns one entry per type that has data, ordered by MAP_GAME_MODE_ORDER.
 * Win/pick rates are simple-averaged across all maps within each type.
 */
export function getHeroMapTypeStats(
    statsData: StatsData,
    gameMode: GameMode,
    tier: string,
    heroSlug: string,
): MapTypeStats[] {
    const maps = getAvailableMaps(statsData, gameMode, tier);

    // Accumulate win/pick rate sums per type
    const buckets: Record<string, { winSum: number; pickSum: number; count: number }> = {};

    for (const m of maps) {
        const type = getMapType(m.key);
        if (!type) continue;
        const heroes = getHeroData(statsData, gameMode, tier, m.key);
        const h = heroes.find((h) => h.slug === heroSlug);
        if (!h) continue;
        if (isInsufficientData(h.winRate) || isInsufficientData(h.pickRate)) continue;
        if (!buckets[type]) buckets[type] = { winSum: 0, pickSum: 0, count: 0 };
        buckets[type].winSum += h.winRate;
        buckets[type].pickSum += h.pickRate;
        buckets[type].count += 1;
    }

    // Build ordered result, skipping types with no data
    const result: MapTypeStats[] = [];
    for (const type of MAP_GAME_MODE_ORDER) {
        const b = buckets[type];
        if (!b || b.count === 0) continue;
        result.push({
            type,
            label: getMapTypeLabel(type),
            winRate: b.winSum / b.count,
            pickRate: b.pickSum / b.count,
            mapCount: b.count,
        });
    }
    return result;
}

/**
 * Aggregate hero stats across all maps of a given game mode type.
 * Win/pick rates are simple-averaged across all maps in the mode.
 */
export function getHeroStatsByMapMode(
    statsData: StatsData,
    gameMode: GameMode,
    tier: string,
    mode: MapGameMode,
): RawHeroStats[] {
    const maps = getAvailableMaps(statsData, gameMode, tier).filter((m) => getMapType(m.key) === mode);
    if (maps.length === 0) return [];

    const buckets: Record<
        string,
        {
            name: string;
            slug: string;
            role: HeroRole;
            subrole: string | null;
            winSum: number;
            pickSum: number;
            banSum: number;
            banCount: number;
            count: number;
        }
    > = {};

    for (const m of maps) {
        const heroes = getHeroData(statsData, gameMode, tier, m.key);
        for (const h of heroes) {
            if (isInsufficientData(h.winRate) || isInsufficientData(h.pickRate)) continue;
            if (!buckets[h.slug])
                buckets[h.slug] = {
                    name: h.name,
                    slug: h.slug,
                    role: h.role,
                    subrole: h.subrole,
                    winSum: 0,
                    pickSum: 0,
                    banSum: 0,
                    banCount: 0,
                    count: 0,
                };
            buckets[h.slug].winSum += h.winRate;
            buckets[h.slug].pickSum += h.pickRate;
            if (h.banRate !== null && h.banRate !== undefined && h.banRate >= 0) {
                buckets[h.slug].banSum += h.banRate;
                buckets[h.slug].banCount += 1;
            }
            buckets[h.slug].count += 1;
        }
    }

    return Object.values(buckets).map((b) => ({
        name: b.name,
        slug: b.slug,
        role: b.role,
        subrole: b.subrole,
        winRate: b.winSum / b.count,
        pickRate: b.pickSum / b.count,
        banRate: b.banCount > 0 ? b.banSum / b.banCount : null,
    }));
}

/** Build the icon-select options list for map dropdowns. */
export interface IconOption {
    value: string;
    label: string;
    icon?: string;
}

export function buildMapOptions(availableMaps: MapInfo[]): IconOption[] {
    return [
        { value: "all", label: "All Maps" },
        ...availableMaps.map((m) => ({ value: m.key, label: m.name, icon: getMapImage(m.key) })),
    ];
}
